import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PhotoIcon from "@mui/icons-material/Photo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { downloadFileHelper } from "features/fileManagement/downloadFileHelper";
import EvidenceViewerDialog from "features/programmesActions/EvidenceViewerDialog";
import { FILE_UPLOAD_TYPE } from "model";
import { useState } from "react";

export type FileItem = {
  guid: string;
  fileEntryGuid: string;
  fileName: string;
  fileUploadType: string;
  creationDate?: string;
  canDelete: boolean;
};

interface Props {
  files: FileItem[];
  onDelete: (guid: string) => Promise<void>;
  isDeletePending?: boolean;
  compact?: boolean;
}

export default function FileItemList({
  files,
  onDelete,
  isDeletePending = false,
  compact = false,
}: Props) {
  const { notify } = useNotification();
  const [previewItem, setPreviewItem] = useState<FileItem | null>(null);

  if (files.length === 0) return null;

  if (compact) {
    return (
      <>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {files.map((item) => {
            const isPhoto =
              item.fileUploadType ===
              FILE_UPLOAD_TYPE.ACTION_EVIDENCE_PHOTO.valueOf();

            return (
              <Box
                key={item.guid}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  py: 0.25,
                }}
              >
                {isPhoto ? (
                  <PhotoIcon
                    sx={{ color: "#2E7831", fontSize: 16, flexShrink: 0 }}
                  />
                ) : (
                  <InsertDriveFileIcon
                    sx={{ color: "#7D5DA3", fontSize: 16, flexShrink: 0 }}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {item.fileName || "Unnamed file"}
                </Typography>
                <Box sx={{ display: "flex", gap: 0, flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => setPreviewItem(item)}
                    sx={{ p: 0.25 }}
                  >
                    <VisibilityIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={async () => {
                      await downloadFileHelper(item.fileEntryGuid, notify);
                    }}
                    sx={{ p: 0.25 }}
                  >
                    <DownloadIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  {item.canDelete && (
                    <IconButton
                      size="small"
                      onClick={async () => {
                        await onDelete(item.guid);
                      }}
                      disabled={isDeletePending}
                      sx={{ p: 0.25 }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
        <EvidenceViewerDialog
          open={!!previewItem}
          fileEntryGuid={previewItem?.fileEntryGuid ?? ""}
          fileName={previewItem?.fileName ?? ""}
          onClose={() => setPreviewItem(null)}
        />
      </>
    );
  }

  return (
    <>
      <List disablePadding>
        {files.map((item) => {
          const isPhoto =
            item.fileUploadType ===
            FILE_UPLOAD_TYPE.ACTION_EVIDENCE_PHOTO.valueOf();

          return (
            <ListItem
              key={item.guid}
              sx={{
                border: "1px solid #F0F0F0",
                borderRadius: "6px",
                mb: 1,
                pl: 1.5,
                pr: item.canDelete ? "116px" : "80px",
                py: 0.75,
                cursor: "pointer",
                "& .MuiListItemSecondaryAction-root": {
                  right: 8,
                },
              }}
              onClick={() => setPreviewItem(item)}
              secondaryAction={
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewItem(item);
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await downloadFileHelper(item.fileEntryGuid, notify);
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  {item.canDelete && (
                    <IconButton
                      size="small"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await onDelete(item.guid);
                      }}
                      disabled={isDeletePending}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {isPhoto ? (
                  <PhotoIcon sx={{ color: "#2E7831" }} />
                ) : (
                  <InsertDriveFileIcon sx={{ color: "#7D5DA3" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.fileName || "Unnamed file"}
                primaryTypographyProps={{ fontSize: "14px", noWrap: true }}
                secondary={
                  item.creationDate
                    ? new Date(item.creationDate).toLocaleDateString()
                    : undefined
                }
                secondaryTypographyProps={{ fontSize: "12px" }}
              />
            </ListItem>
          );
        })}
      </List>
      <EvidenceViewerDialog
        open={!!previewItem}
        fileEntryGuid={previewItem?.fileEntryGuid ?? ""}
        fileName={previewItem?.fileName ?? ""}
        onClose={() => setPreviewItem(null)}
      />
    </>
  );
}
